import styled from "@emotion/styled";
import { useLocation } from "react-router-dom";
import {
  Button,
  CopyToClipboard,
  DropdownMenu,
  IconShare,
  List,
  Stack,
  Text,
} from "atoms";
import type { Artist } from "features/reviewable";
import { paths } from "utils/paths";

interface ArtistProfileShareMenuProps {
  artist: Artist;
  isOpen: boolean;
  toggle: () => void;
}

function ArtistProfileShareMenu({
  artist,
  isOpen,
  toggle,
}: ArtistProfileShareMenuProps) {
  const { pathname } = useLocation();

  const { origin, href } = window.location;
  const profileUrl = `${origin}${paths.artistProfile(artist.id)}`;

  let currentTabLabel = "";
  if (pathname.endsWith("/about")) {
    currentTabLabel = "About";
  } else if (pathname.endsWith("/updates")) {
    currentTabLabel = "Updates";
  } else if (pathname.endsWith("/reviews")) {
    currentTabLabel = "Reviews";
  } else if (pathname.includes("/portfolio/item")) {
    currentTabLabel = "Item";
  } else if (pathname.includes("/portfolio/gallery")) {
    currentTabLabel = "Gallery";
  } else if (pathname.includes("/portfolio")) {
    currentTabLabel = "Portfolio";
  } else if (pathname.match(/\/shop\/\w+/)) {
    currentTabLabel = "Product";
  } else if (pathname.includes("/shop")) {
    currentTabLabel = "Shop";
  }

  return (
    <ShareMenuWrapper>
      <ShareMenuToggle
        type="button"
        size="xxs"
        onClick={toggle}
        aria-label={`${isOpen ? "Close" : "Open"} share menu`}
        aria-controls="#share"
        aria-expanded={isOpen}
        variant="primary"
      >
        <IconShare aria-hidden="true" />
      </ShareMenuToggle>

      <ShareMenuDropdown isOpen={isOpen} id="share">
        <Stack gap="sm">
          <Text bold size="xxxs" textTransform="uppercase">
            Share Options
          </Text>
          <List type="blank">
            <Stack gap="xxs">
              <div>
                <li>
                  <CopyToClipboard
                    buttonText="Copy Link to Profile"
                    value={profileUrl}
                    sendShareEvent
                    onClick={toggle}
                  />
                </li>
              </div>
              {artist.betaProfile === "PUBLISHED" && currentTabLabel && (
                <div>
                  <li>
                    <CopyToClipboard
                      buttonText={`Copy Link to ${currentTabLabel}`}
                      value={href}
                      sendShareEvent
                      onClick={toggle}
                    />
                  </li>
                </div>
              )}
            </Stack>
          </List>
        </Stack>
      </ShareMenuDropdown>
    </ShareMenuWrapper>
  );
}

const ShareMenuWrapper = styled.div(({ theme }) => ({
  display: "block",
  position: "relative",
  [theme.breakpoints.maxDesktop]: {
    position: "absolute",
    top: theme.spacing.md,
    right: theme.spacing.md,
  },
}));

interface WithIsOpen {
  isOpen: boolean;
}

const ShareMenuToggle = styled(Button)({
  alignItems: "center",
  display: "block",
  justifyContent: "center",
  paddingLeft: 10,
  paddingRight: 10,
  svg: {
    pointerEvents: "none",
    fontSize: "1em",
    transform: "scale(1.2)",
  },
});

const ShareMenuDropdown = styled(DropdownMenu)<WithIsOpen>({
  maxWidth: "250px",
});

export { ArtistProfileShareMenu };
